import React, {FC} from 'react';
import {connect} from 'react-redux';

import {ResolveDispatchProps} from '../../../../../../../../src/helpers/reduxHelpers';
import {
  makeGetContactsManagerPanel,
  makeGetContactsManagerParameters,
  makeGetContactsManagerSearchState,
} from '../../../../../../../../src/store/panels/contacts/contactsManager/contactsManagerPanelSelectors';
import {ContactsManagerParametersTypesEnum} from '../../../../../../../../src/store/panels/contacts/contactsManager/parameters/contactsManagerParameters';
import {closeSidePanelAction} from '../../../../../../../../src/store/panels/contacts/contactsManager/sidePanel/contactsManagerSidePanelActions';
import {contactsManagerTableAccountIdsRemoveSelectedAction} from '../../../../../../../../src/store/panels/contacts/contactsManager/table/contactsManagerTableActions';
import {ContactsManagerPanelId} from '../../../../../../../../src/store/panels/panelStateBase';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../../../../../srcCommon/helpers/reselectHelpers';
import {useInitialEffect} from '../../../../../../../shared/helpers/src/react/hooks/useEffectHelpers';
import {Handler} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {AccountDetailsSidePanel} from './accountDetailsSidePanel';

/*
 * Props.
 */

interface AccountDetailsSidePanelContainerPublicProps {
  panelId: ContactsManagerPanelId;
}

interface AccountDetailsSidePanelContainerMergeProps {
  onUnmount: Handler;
}

type AccountDetailsSidePanelContainerStateProps = ResolveStateProps<typeof mapStateToProps>;
type AccountDetailsSidePanelContainerComponentProps = AccountDetailsSidePanelContainerPublicProps &
  AccountDetailsSidePanelContainerStateProps &
  AccountDetailsSidePanelContainerMergeProps;

/*
 * Selectors.
 */

function makeGetAccountId() {
  return createSelector([makeGetContactsManagerPanel()], (panel) => {
    const {searchState, parameters} = panel;
    if (parameters.type === ContactsManagerParametersTypesEnum.ACCOUNTS) {
      return parameters.accountId;
    }
    if (searchState?.accountsTableState) {
      return searchState.accountsTableState.selectedAccountIds[0];
    }
    return undefined;
  });
}

/*
 * Component.
 */

export const AccountDetailsSidePanelContainerComponent: FC<AccountDetailsSidePanelContainerComponentProps> = (
  props,
) => {
  const {panelId, accountId, parameters, onUnmount} = props;
  const location = parameters.type;

  // Listen for when the side panel unmounts and do any clean up needed.
  useInitialEffect(() => () => onUnmount());

  return <AccountDetailsSidePanel panelId={panelId} accountId={accountId} location={location} />;
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<AccountDetailsSidePanelContainerPublicProps>()(() => ({
  accountId: makeGetAccountId(),
  searchState: makeGetContactsManagerSearchState(),
  parameters: makeGetContactsManagerParameters(),
}));

const mapDispatchToProps = makeMapDispatchToProps<AccountDetailsSidePanelContainerPublicProps>()(
  ({panelId}) => ({
    onCloseSidePanel: closeSidePanelAction,
    onRemoveSelectedAccountIds: (accountIds: ReadonlyArray<number>) =>
      contactsManagerTableAccountIdsRemoveSelectedAction(panelId, accountIds),
  }),
);

function mergeProps(
  stateProps: ResolveStateProps<typeof mapStateToProps>,
  dispatchProps: ResolveDispatchProps<typeof mapDispatchToProps>,
  ownProps: AccountDetailsSidePanelContainerPublicProps,
) {
  const {searchState} = stateProps;
  const {onRemoveSelectedAccountIds, onCloseSidePanel} = dispatchProps;

  const onUnmount = () => {
    // If we are searching for accounts, we will need to remove the selected account from the state.
    if (searchState?.accountsTableState) {
      onRemoveSelectedAccountIds(searchState?.accountsTableState.accountIds);
    }
    onCloseSidePanel();
  };

  return {
    ...ownProps,
    ...stateProps,
    onUnmount,
  };
}

export const AccountDetailsSidePanelContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)(AccountDetailsSidePanelContainerComponent);
